/** @file Reversible commands for Scene CRUD operations (add/delete/reorder/duration). */
import { appStore } from "./state.js";
import { shortId } from "./ids.js";
import { cloneScene, projectSceneToTokens } from "./play.js";

/** @typedef {import("./history.js").Command} Command */
/** @typedef {import("./play.js").Scene} Scene */

/**
 * Build a new scene by snapshotting the currently-active scene (tokens + ball)
 * and copying its visibleArrowIds. Used by addSceneCommand.
 * @param {Scene} source
 * @returns {Scene}
 */
function snapshotFromScene(source) {
  return {
    id: shortId(),
    tokens: Object.fromEntries(
      Object.entries(source.tokens).map(([id, p]) => [id, { x: p.x, y: p.y }]),
    ),
    ball: source.ball ? { x: source.ball.x, y: source.ball.y } : null,
    visibleArrowIds: [...source.visibleArrowIds],
    durationMs: 2000,
  };
}

/**
 * Append a new scene after the active one (which is a snapshot of the active).
 * @returns {Command}
 */
export function addSceneCommand() {
  const state = appStore.get();
  const active = state.scenes[state.activeSceneIndex] ?? state.scenes[0];
  if (!active) {
    throw new Error("addSceneCommand: no scenes in state");
  }
  const newScene = snapshotFromScene(active);
  const prevActiveIndex = state.activeSceneIndex;
  const insertIndex = prevActiveIndex + 1;

  return {
    type: "addScene",
    do: () =>
      appStore.set((s) => {
        const scenes = [...s.scenes];
        scenes.splice(insertIndex, 0, cloneScene(newScene));
        return {
          scenes,
          activeSceneIndex: insertIndex,
          tokens: projectSceneToTokens(newScene, s.tokens),
        };
      }),
    undo: () =>
      appStore.set((s) => {
        const scenes = s.scenes.filter((sc) => sc.id !== newScene.id);
        const prevScene = scenes[prevActiveIndex] ?? scenes[0];
        return {
          scenes,
          activeSceneIndex: prevActiveIndex,
          tokens: prevScene ? projectSceneToTokens(prevScene, s.tokens) : s.tokens,
        };
      }),
  };
}

/**
 * Delete scene at index. Returns null if only 1 scene remains.
 * @param {number} index
 * @returns {Command | null}
 */
export function deleteSceneCommand(index) {
  const state = appStore.get();
  if (state.scenes.length <= 1) {
    return null;
  }
  const removed = state.scenes[index];
  if (!removed) {
    return null;
  }
  const snapshot = cloneScene(removed);
  const prevActiveIndex = state.activeSceneIndex;
  const nextActiveIndex =
    prevActiveIndex >= index ? Math.max(0, prevActiveIndex - 1) : prevActiveIndex;

  return {
    type: "deleteScene",
    do: () =>
      appStore.set((s) => {
        const scenes = s.scenes.filter((_, i) => i !== index);
        const active = scenes[nextActiveIndex] ?? scenes[0];
        return {
          scenes,
          activeSceneIndex: nextActiveIndex,
          tokens: active ? projectSceneToTokens(active, s.tokens) : s.tokens,
        };
      }),
    undo: () =>
      appStore.set((s) => {
        const scenes = [...s.scenes];
        scenes.splice(index, 0, cloneScene(snapshot));
        const active = scenes[prevActiveIndex] ?? scenes[0];
        return {
          scenes,
          activeSceneIndex: prevActiveIndex,
          tokens: active ? projectSceneToTokens(active, s.tokens) : s.tokens,
        };
      }),
  };
}

/**
 * Move scene from one index to another.
 * @param {number} from
 * @param {number} to
 * @returns {Command | null}
 */
export function reorderSceneCommand(from, to) {
  const state = appStore.get();
  if (
    from === to ||
    from < 0 ||
    from >= state.scenes.length ||
    to < 0 ||
    to >= state.scenes.length
  ) {
    return null;
  }
  const prevActiveIndex = state.activeSceneIndex;

  /**
   * @param {Scene[]} scenes
   * @param {number} a
   * @param {number} b
   * @returns {Scene[]}
   */
  function move(scenes, a, b) {
    const out = [...scenes];
    const [item] = out.splice(a, 1);
    if (item) {
      out.splice(b, 0, item);
    }
    return out;
  }

  /**
   * @param {number} a
   * @param {number} b
   * @returns {number}
   */
  function mapActive(a, b) {
    if (prevActiveIndex === a) {
      return b;
    }
    if (a < prevActiveIndex && b >= prevActiveIndex) {
      return prevActiveIndex - 1;
    }
    if (a > prevActiveIndex && b <= prevActiveIndex) {
      return prevActiveIndex + 1;
    }
    return prevActiveIndex;
  }

  return {
    type: "reorderScene",
    do: () =>
      appStore.set((s) => ({
        scenes: move(s.scenes, from, to),
        activeSceneIndex: mapActive(from, to),
      })),
    undo: () =>
      appStore.set((s) => ({
        scenes: move(s.scenes, to, from),
        activeSceneIndex: prevActiveIndex,
      })),
  };
}

/**
 * Set active scene (NOT undoable — navigation, not edit).
 * Projects the scene's positions onto state.tokens.
 * @param {number} index
 */
export function setActiveSceneCommand(index) {
  const state = appStore.get();
  if (index < 0 || index >= state.scenes.length) {
    return;
  }
  const scene = state.scenes[index];
  if (!scene) {
    return;
  }
  appStore.set({
    activeSceneIndex: index,
    tokens: projectSceneToTokens(scene, state.tokens),
  });
}

/**
 * Update a scene's duration (undoable).
 * @param {number} index
 * @param {number} from
 * @param {number} to
 * @returns {Command | null}
 */
export function updateSceneDurationCommand(index, from, to) {
  if (from === to) {
    return null;
  }
  /**
   * @param {number} value
   * @returns {(s: import("./state.js").AppState) => Partial<import("./state.js").AppState>}
   */
  const patch = (value) => (s) => ({
    scenes: s.scenes.map((sc, i) => (i === index ? { ...sc, durationMs: value } : sc)),
  });
  return {
    type: "updateSceneDuration",
    do: () => appStore.set(patch(to)),
    undo: () => appStore.set(patch(from)),
  };
}

/**
 * Toggle arrow visibility in a specific scene.
 * @param {string} arrowId
 * @param {number} sceneIdx
 * @returns {Command | null}
 */
export function toggleArrowVisibleInSceneCommand(arrowId, sceneIdx) {
  const state = appStore.get();
  const scene = state.scenes[sceneIdx];
  if (!scene) {
    return null;
  }
  const wasVisible = scene.visibleArrowIds.includes(arrowId);

  /**
   * @param {boolean} makeVisible
   * @returns {(s: import("./state.js").AppState) => Partial<import("./state.js").AppState>}
   */
  const patch = (makeVisible) => (s) => ({
    scenes: s.scenes.map((sc, i) => {
      if (i !== sceneIdx) {
        return sc;
      }
      const set = new Set(sc.visibleArrowIds);
      if (makeVisible) {
        set.add(arrowId);
      } else {
        set.delete(arrowId);
      }
      return { ...sc, visibleArrowIds: [...set] };
    }),
  });

  return {
    type: "toggleArrowVisibleInScene",
    do: () => appStore.set(patch(!wasVisible)),
    undo: () => appStore.set(patch(wasVisible)),
  };
}

/**
 * Show arrow in every scene.
 * @param {string} arrowId
 * @returns {Command | null}
 */
export function setArrowVisibleInAllScenesCommand(arrowId) {
  const state = appStore.get();
  const prev = state.scenes.map((sc) => sc.visibleArrowIds.includes(arrowId));
  return {
    type: "setArrowVisibleInAllScenes",
    do: () =>
      appStore.set((s) => ({
        scenes: s.scenes.map((sc) => {
          if (sc.visibleArrowIds.includes(arrowId)) {
            return sc;
          }
          return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, arrowId] };
        }),
      })),
    undo: () =>
      appStore.set((s) => ({
        scenes: s.scenes.map((sc, i) => {
          const shouldBeVisible = prev[i] ?? false;
          const isVisible = sc.visibleArrowIds.includes(arrowId);
          if (shouldBeVisible === isVisible) {
            return sc;
          }
          if (shouldBeVisible) {
            return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, arrowId] };
          }
          return {
            ...sc,
            visibleArrowIds: sc.visibleArrowIds.filter((id) => id !== arrowId),
          };
        }),
      })),
  };
}

/**
 * Show arrow only in the active scene; remove from all others.
 * @param {string} arrowId
 * @returns {Command | null}
 */
export function setArrowVisibleOnlyInActiveSceneCommand(arrowId) {
  const state = appStore.get();
  const activeIdx = state.activeSceneIndex;
  const prev = state.scenes.map((sc) => sc.visibleArrowIds.includes(arrowId));

  return {
    type: "setArrowVisibleOnlyInActiveScene",
    do: () =>
      appStore.set((s) => ({
        scenes: s.scenes.map((sc, i) => {
          const shouldBeVisible = i === activeIdx;
          const isVisible = sc.visibleArrowIds.includes(arrowId);
          if (shouldBeVisible === isVisible) {
            return sc;
          }
          if (shouldBeVisible) {
            return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, arrowId] };
          }
          return {
            ...sc,
            visibleArrowIds: sc.visibleArrowIds.filter((id) => id !== arrowId),
          };
        }),
      })),
    undo: () =>
      appStore.set((s) => ({
        scenes: s.scenes.map((sc, i) => {
          const shouldBeVisible = prev[i] ?? false;
          const isVisible = sc.visibleArrowIds.includes(arrowId);
          if (shouldBeVisible === isVisible) {
            return sc;
          }
          if (shouldBeVisible) {
            return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, arrowId] };
          }
          return {
            ...sc,
            visibleArrowIds: sc.visibleArrowIds.filter((id) => id !== arrowId),
          };
        }),
      })),
  };
}
